import type { QuizTimerMode } from '../types';

const STORAGE_KEY = 'smym-quiz-play-draft';

export interface QuizPlayDraft {
  sessionId: string;
  campaignId: string;
  currentQuestionIndex: number;
  answers: number[];
  startedAt: string;
  quizFinished: boolean;
  timerSeconds: number;
  timerMode: QuizTimerMode;
  questionStartedAt: string[];
  completedAt: string | null;
  /** Display order shown to the player: displayOrder[i] is the original (scoring) index shown at position i. */
  displayOrder: number[];
  /** Per-question option display order, keyed by original question index: optionOrders[qi][j] is the original option index shown at position j. */
  optionOrders: number[][];
}

export function loadQuizPlayDraft(): QuizPlayDraft | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as QuizPlayDraft;
    if (
      typeof parsed.sessionId !== 'string' ||
      typeof parsed.campaignId !== 'string' ||
      typeof parsed.startedAt !== 'string' ||
      !Array.isArray(parsed.answers) ||
      !Array.isArray(parsed.questionStartedAt) ||
      (parsed.timerMode !== 'per_round' && parsed.timerMode !== 'per_question') ||
      !Array.isArray(parsed.displayOrder) ||
      !Array.isArray(parsed.optionOrders)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

/** Fisher-Yates shuffle of question positions, used to randomize display order without touching scoring order. */
export function createShuffledOrder(length: number): number[] {
  const order = Array.from({ length }, (_, i) => i);
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [order[i], order[j]] = [order[j]!, order[i]!];
  }
  return order;
}

/** True if the given display order is a valid permutation of [0, length). */
export function isValidDisplayOrder(order: number[], length: number): boolean {
  if (order.length !== length) return false;
  const seen = new Set(order);
  if (seen.size !== length) return false;
  return order.every((value) => Number.isInteger(value) && value >= 0 && value < length);
}

/** Shuffled option order per question, used to randomize option display without touching which index scores as correct. */
export function createOptionOrders(questions: { options: string[] }[]): number[][] {
  return questions.map((q) => createShuffledOrder(q.options.length));
}

/** True if orders has one valid option permutation per question, matching each question's option count. */
export function isValidOptionOrders(orders: number[][], questions: { options: string[] }[]): boolean {
  if (orders.length !== questions.length) return false;
  return orders.every((order, i) => isValidDisplayOrder(order, questions[i]!.options.length));
}

export function saveQuizPlayDraft(draft: QuizPlayDraft): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  } catch {
    // sessionStorage may be unavailable; ignore
  }
}

export function clearQuizPlayDraft(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function computeSecondsLeft(startedAt: string, timerSeconds: number): number {
  const elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / 1000);
  return Math.max(0, timerSeconds - elapsed);
}

/** Pad sparse in-progress answers to full length; unanswered slots use -1. */
export function normalizeQuizAnswers(answers: number[], questionCount: number): number[] {
  return Array.from({ length: questionCount }, (_, i) => {
    const value = answers[i];
    if (typeof value === 'number' && Number.isInteger(value) && value >= 0) return value;
    return -1;
  });
}
